/* eslint-disable lit/binding-positions, lit/no-invalid-html  */
import { html } from 'lit/static-html.js';
import { nothing, type PropertyValues } from 'lit';
import { ifDefined } from 'lit/directives/if-defined.js';
import { classMap } from 'lit/directives/class-map.js';

import { PolymorphicTagMixin } from '../../mixins/polymorphic-tag';
import { FormControl } from '../form-control';
import styles from './button.styles';

export type ButtonVariant =
	| 'primary'
	| 'secondary'
	| 'tertiary'
	| 'warning'
	| 'danger'
	| 'icon'
	| 'icon-secondary';

export type ButtonSize = 'small' | 'default' | 'large';

export type ButtonType = 'button' | 'reset' | 'submit';

type ButtonTarget = '_blank' | '_parent' | '_self' | '_top';

export interface ButtonProps {
	disabled?: boolean;
	loading?: boolean;
	size?: ButtonSize;
	type: ButtonType;
	variant: ButtonVariant;
	href?: string;
	download?: string;
	tabindex?: string;
	target?: ButtonTarget;
}

export class Button extends PolymorphicTagMixin(
	FormControl,
	['button', 'a'],
	'button',
) {
	static override styles = [styles()];

	static override get properties() {
		return {
			...FormControl.properties,
			role: { type: String, reflect: true },
			loading: { type: Boolean },
			disabled: { type: Boolean, reflect: true },
			variant: { type: String },
			size: { type: String },
			tabindex: { type: String },
			type: { type: String },
			// anchor attributes
			href: { type: String },
			target: { type: String },
			download: { type: String },
		};
	}

	/**
	 * The button is waiting for some operation to be finished.
	 */
	declare loading: ButtonProps['loading'];

	/**
	 * Should the button be disabled.
	 */
	declare disabled: ButtonProps['disabled'];

	/**
	 * Button variant.
	 */
	declare variant: ButtonProps['variant'];

	/**
	 * Button size.
	 */
	declare size: ButtonProps['size'];

	/**
	 * Allow making HTML elements focusable, allow or prevent them from being sequentially
	 * focusable (usually with the Tab key, hence the name) and determine their relative ordering
	 * for sequential focus navigation
	 */
	declare tabindex: ButtonProps['tabindex'];

	/**
	 * Specifies the type of button.
	 */
	declare type: ButtonProps['type'];

	/**
	 * When set, the underlying button will be rendered as an `<a>` with this `href` instead of a `<button>`.
	 */
	declare href: ButtonProps['href'];

	/**
	 * Tell the browser where to open the link. Only used when `href` is set.
	 * */
	declare target: ButtonProps['target'];

	/**
	 * Causes the browser to treat the linked URL as a download. Only used when `href` is set.
	 */
	declare download: ButtonProps['download'];

	constructor() {
		super();

		this.as = 'button';
		this.disabled = false;
		this.loading = false;
		this.size = 'default';
		this.type = 'button';
		this.variant = 'secondary';

		this.target = '_self';
	}

	override connectedCallback(): void {
		super.connectedCallback();

		this.setAttribute('role', 'button');
		this.addEventListener('click', this.#handleClick);
	}

	override disconnectedCallback(): void {
		super.disconnectedCallback();

		this.removeEventListener('click', this.#handleClick);
	}

	protected override update(changedProperties: PropertyValues<this>): void {
		super.update(changedProperties);

		if (changedProperties.has('href')) {
			this.as = this.#isLink() ? 'a' : 'button';
			this.role = this.#isLink() ? 'link' : 'button';
		}
	}

	protected override updated(changedProperties: PropertyValues<this>): void {
		super.updated(changedProperties);

		if (changedProperties.has('disabled') || changedProperties.has('loading')) {
			this.#setDisabled();
		}
	}

	override render() {
		const isLink = this.#isLink();

		return html`
			<${this.tag}
				class=${classMap({
					button: true,

					'-primary': this.variant === 'primary',
					'-secondary': this.variant === 'secondary',
					'-tertiary': this.variant === 'tertiary',
					'-warning': this.variant === 'warning',
					'-danger': this.variant === 'danger',
					'-icon': this.variant === 'icon',
					'-icon-secondary': this.variant === 'icon-secondary',

					'-small': this.size === 'small',
					'-default': this.size === 'default',
					'-large': this.size === 'large',

					'is-loading': Boolean(this.loading),
					'is-disabled': this.#isDisabled(),
				})}
				type=${ifDefined(isLink ? undefined : this.type)}
				?disabled="${this.#isDisabled()}"
				aria-disabled=${this.disabled ? 'true' : 'false'}
				role=${ifDefined(isLink ? 'link' : 'button')}
				tabindex=${ifDefined(this.#getTabIndex())}
				href=${ifDefined(isLink ? this.href : undefined)}
				target=${ifDefined(isLink ? this.target : undefined)}
				download=${ifDefined(isLink ? this.download : undefined)}
				rel=${ifDefined(isLink && this.target ? 'noreferrer noopener' : undefined)}
			>
				<!-- TODO: Replace this with the loading dots component as soon as it is ready. -->
				${
					this.loading && !['icon', 'icon-secondary'].includes(this.variant)
						? html`<span class="loader">&#x25cf;&#x25cf;&#x25cf;</span>`
						: nothing
				}
				<slot name="leading"></slot>
				<slot></slot>
				<slot name="trailing"></slot>
			</${this.tag}>
		`;
	}

	#isDisabled = () => {
		return Boolean(this.disabled) || Boolean(this.loading);
	};

	#setDisabled = () => {
		const disabled = this.#isDisabled();

		this.setAttribute('aria-disabled', String(disabled));
	};

	#getTabIndex = () => {
		if (this.#isLink()) {
			if (this.disabled) {
				return '-1';
			}

			if (this.tabindex != null) {
				return this.tabindex;
			}

			return '0';
		}

		return this.tabindex ?? undefined;
	};

	#handleClick = (event: MouseEvent) => {
		if (this.disabled || this.loading) {
			event.preventDefault();
			event.stopPropagation();
			return;
		}

		/**
		 * https://developer.mozilla.org/en-US/docs/Web/API/HTMLFormElement/submit
		 */
		if (this.type === 'submit') {
			this.form?.requestSubmit();
		}

		if (this.type === 'reset') {
			this.form?.reset();
		}
	};

	#isLink() {
		return Boolean(this.href);
	}
}

window.customElements.define('m-button', Button);

declare global {
	interface HTMLElementTagNameMap {
		'm-button': Button;
	}
}
